import type { QueryOptions } from '@apollo/client';
import { type Zone, brandAndCountryFromZone } from '@seek/audience-zones';
import {
  createClient as createGraphqlClient,
  createMockClient,
  type ClientOptions,
} from '@seek/ca-graphql-client';
import { browserHttpLink } from '@seek/ca-graphql-client/browser';
import { nodeHttpLink } from '@seek/ca-graphql-client/node';

import { CANDIDATE_GRAPH_DARK_PROD_URL } from 'src/config';
import notificationsMockOverrides from 'src/modules/graphql/notifications/notifications-mock-overrides';
import type {
  ChangeEmailStartModel,
  ChangeEmailVerifyModel,
} from 'src/reducers/user/actionCreators';

import { isWindowDefined } from '../window';

import { CHANGE_EMAIL } from './account/changeEmail.graphql';
import { CHANGE_EMAIL_START } from './account/changeEmailStart.graphql';
import { CHANGE_EMAIL_VERIFY } from './account/changeEmailVerify.graphql';
import { DELETE_ACCOUNT } from './account/deleteAccount.graphql';
import { handleAuthError, handleValidationErrors } from './errors';
import { GET_USER_QUERY } from './getUser.graphql';
import resolverOverrides from './resolver-overrides';
import { visibilityMockOverrides } from './visibility/visibility-mock-overrides';

export const createClient = (
  authOptions: any,
  appName: string,
  mock: boolean,
  zone: Zone,
  isServer?: boolean,
) => {
  const queryString = isWindowDefined ? window.location.href.split('?')[1] : '';
  const searchParams = new URLSearchParams(queryString);
  const darkProd = searchParams.get('darkProd');
  const darkProdBranch = darkProd !== 'master' ? `?branch=${darkProd}` : '';
  const [brand, country] = brandAndCountryFromZone(zone) || ['SEEK', 'AU'];
  const graphqlClient =
    mock === true
      ? createMockClient({
          mocks: {
            ...visibilityMockOverrides,
            ...notificationsMockOverrides,
          },
          resolvers: resolverOverrides,
        })
      : createGraphqlClient({
          brand: brand.toLowerCase() as ClientOptions['brand'],
          country: country as ClientOptions['country'],
          headers: { Accept: 'application/features.seek.all+json' },
          appName,
          ...(CANDIDATE_GRAPH_DARK_PROD_URL && darkProd
            ? {
                graphqlEndpointOverride: `${CANDIDATE_GRAPH_DARK_PROD_URL}${darkProdBranch}`,
              }
            : {}),
          httpLink: isServer ? nodeHttpLink : browserHttpLink,
          authOptions,
        });
  const mutateGql = async (options: {
    mutation: any;
    variables?: Record<string, unknown>;
  }) =>
    graphqlClient
      .mutate(options)
      .catch(handleAuthError)
      .catch(handleValidationErrors);

  const queryGql = (options: QueryOptions) =>
    graphqlClient.query({ fetchPolicy: 'no-cache', ...options });

  const changeEmail = ({
    newEmailAddress,
    password,
  }: {
    newEmailAddress: string;
    password: string;
  }) =>
    mutateGql({
      mutation: CHANGE_EMAIL,
      variables: {
        emailChange: {
          newEmailAddress,
          password,
        },
      },
    }).then((result) => result?.data?.changeEmailAddress);

  const deleteAccount = () =>
    mutateGql({
      mutation: DELETE_ACCOUNT,
      variables: { accountDelete: {} },
    }).then((result) => result?.data?.deleteAccount);

  const getUser = () =>
    queryGql({
      query: GET_USER_QUERY,
    });

  const changeEmailStart = (input: ChangeEmailStartModel) =>
    mutateGql({
      mutation: CHANGE_EMAIL_START,
      variables: {
        emailChangeStart: {
          newEmailAddress: input.newEmailAddress,
        },
      },
    }).then((result) => result?.data?.changeEmailStart);

  const changeEmailVerify = (input: ChangeEmailVerifyModel) =>
    mutateGql({
      mutation: CHANGE_EMAIL_VERIFY,
      variables: {
        emailChangeVerify: {
          newEmailAddress: input.newEmailAddress,
          verificationCode: input.verificationCode,
          brand: input.brand,
          country: input.country,
          language: input.language,
        },
      },
    }).then((result) => result?.data?.changeEmailVerify);

  return {
    client: graphqlClient,
    changeEmail,
    deleteAccount,
    getUser,
    changeEmailStart,
    changeEmailVerify,
  };
};
